/** Expansion preferences survive calendar remounts and are keyed by group value,
 * never by the display label (multiple groups can be named "No Team"). */
export type GroupToggle = {
  isExpanded: () => boolean;
  toggle: () => void;
};

export function createResourceGroups() {
  let snapshot: ReadonlyMap<string, boolean> = new Map();
  const listeners = new Set<() => void>();
  const mounted = new Map<string, GroupToggle>();
  const pending = new Set<string>();

  const remember = (id: string, expanded: boolean) => {
    if (snapshot.get(id) === expanded) return;
    snapshot = new Map(snapshot).set(id, expanded);
    listeners.forEach((listener) => listener());
  };
  const reconcile = (id: string) => {
    const control = mounted.get(id);
    if (!control || pending.has(id)) return;
    if (control.isExpanded() !== snapshot.get(id)) {
      pending.add(id);
      control.toggle();
    }
  };
  const setExpanded = (id: string, expanded: boolean) => {
    remember(id, expanded);
    reconcile(id);
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setExpanded,
    register(id: string, control: GroupToggle, initiallyExpanded: boolean) {
      mounted.set(id, control);
      pending.delete(id);
      setExpanded(id, snapshot.get(id) ?? initiallyExpanded);
      return () => {
        if (mounted.get(id) === control) {
          mounted.delete(id);
          pending.delete(id);
        }
      };
    },
    // Follow native keyboard/API changes as well as our accessible buttons.
    observe(id: string, expanded: boolean) {
      if (pending.delete(id)) {
        // React can commit the native toggle after another request arrived.
        // Apply that newer intent instead of replacing it with the older commit.
        reconcile(id);
      } else {
        remember(id, expanded);
      }
    },
  };
}

export type ResourceGroups = ReturnType<typeof createResourceGroups>;
